import React, { useState, useEffect, useCallback } from "react";
import "../styles/ManageMember.css";
import "../styles/ManageAdvertisingAgent.css";
import SideBar from "../components/SideBar";
import { useNavigate } from "react-router-dom";
import { advertisingAgentAPI } from "../services/advertisingAgentService";
import {
  formatToDDMMYYYY,
  convertHTMLDateToDDMMYYYY,
  convertDDMMYYYYToHTMLDate,
} from "../utils/dateUtils";
import { agentService } from "../services/AgentServices";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AdvertisingAgentApprovedHistory() {
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [approvedDate, setApprovedDate] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalApprovedAgents, setTotalApprovedAgents] = useState(0);
  const [filteredAgentsCount, setFilteredAgentsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Modal State
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAgentForCard, setSelectedAgentForCard] = useState(null);
  const [availableLegendaryCards, setAvailableLegendaryCards] = useState([]);
  const [availableMiniCards, setAvailableMiniCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState("");
  const [isMiniAgent, setIsMiniAgent] = useState(false);

  useEffect(() => {
    fetchAvailableLegendaryCards();
  }, []);

  const fetchAvailableLegendaryCards = async () => {
    try {
      const response = await agentService.getAvailableSeniorLegendaryCards();
      if (response.success && Array.isArray(response.data)) {
        setAvailableLegendaryCards(response.data);
      }
    } catch (error) {
      console.error("Error fetching legendary cards:", error);
    }
  };

  const fetchAvailableMiniCards = async (agentId) => {
    try {
      const response = await agentService.getAvailableMiniCampaignCards(agentId);
      if (response.success && Array.isArray(response.data)) {
        setAvailableMiniCards(response.data);
      }
    } catch (error) {
      console.error("Error fetching mini cards:", error);
    }
  };

  const handleAssignClick = (e, application) => {
    e.stopPropagation(); // prevent row click
    setSelectedAgentForCard(application);
    const isMini = application.agent_type === "mini";
    setIsMiniAgent(isMini);
    setAssignModalOpen(true);
    setSelectedCard("");
    
    if (isMini) {
      const agentId = application.customer_details?.user_id || application.user_id;
      if (agentId) fetchAvailableMiniCards(agentId);
    }
  };

  const handleAssignCard = async () => {
    if (!selectedCard) {
      alert("Please select a card first.");
      return;
    }
    
    try {
      // API call to backend using customer_details user_id (or application user_id)
      const agentId = selectedAgentForCard.customer_details?.user_id || selectedAgentForCard.user_id;
      
      let response;
      if (isMiniAgent) {
        response = await agentService.assignMiniCampaignCard(
          agentId, 
          selectedCard
        );
      } else {
        response = await agentService.assignSeniorLegendaryCard(
          agentId, 
          selectedCard
        );
      }

      if (response.success) {
        setSnackMessage("Card assigned successfully!");
        setSnackSeverity("success");
        setSnackOpen(true);
        setAssignModalOpen(false);
        // Refresh cards
        if (isMiniAgent) {
          fetchAvailableMiniCards(agentId);
        } else {
          fetchAvailableLegendaryCards();
        }
      } else {
        setSnackMessage(response.message || "Failed to assign card");
        setSnackSeverity("error");
        setSnackOpen(true);
      }
    } catch (error) {
      console.error("Error assigning card:", error);
      setSnackMessage(error.message || "Failed to assign card");
      setSnackSeverity("error");
      setSnackOpen(true);
    }
  };

  // Memoized fetch function
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Prepare filters
      const filters = {
        page: currentPage,
        per_page: itemsPerPage,
        status: "approved",
        ...(type !== "all" && { agent_type: type }),
        ...(search && { search: search }),
      };

      // Add date filter if specified
      if (approvedDate !== "all") {
        const dateParts = approvedDate.split('/');
        if (dateParts.length === 3) {
          const apiDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
          filters.reviewed_at = apiDate;
        }
      }

      console.log("Fetching with filters:", filters); // Debug log

      const response = await advertisingAgentAPI.getAgentApplications(filters);
      
      console.log("API Response:", response); // Debug log
      
      setApplications(response.applications || []);
      
      // Set pagination info
      if (response.pagination) {
        const total = response.pagination.total || 0;
        setTotalApprovedAgents(total);
        setFilteredAgentsCount(total);
        
        // Calculate total pages
        const pages = Math.ceil(total / itemsPerPage);
        setTotalPages(pages);
        console.log("Total pages calculated:", pages, "from total:", total); // Debug log
      } else {
        // Fallback if no pagination data
        const apps = response.applications || [];
        setApplications(apps);
        setFilteredAgentsCount(apps.length);
        setTotalPages(Math.ceil(apps.length / itemsPerPage));
      }
    } catch (err) {
      setError("Failed to fetch approved applications");
      console.error("Error fetching approved applications:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, type, search, approvedDate, itemsPerPage]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Reset to page 1 when filters change (except currentPage)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // Don't fetch here - let the above useEffect handle it when currentPage changes
  }, [type, search, approvedDate]);

  // Separate effect to handle initial filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchApplications();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [type, search, approvedDate, currentPage, fetchApplications]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  // Handle date filter change
  const handleDateChange = (e) => {
    const htmlDate = e.target.value;
    if (!htmlDate) {
      setApprovedDate("all");
    } else {
      const ddmmyyyyDate = convertHTMLDateToDDMMYYYY(htmlDate);
      setApprovedDate(ddmmyyyyDate);
    }
  };

  // Get current HTML date value for the input
  const getHTMLDateValue = () => {
    return approvedDate === "all"
      ? ""
      : convertDDMMYYYYToHTMLDate(approvedDate);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    console.log("Changing to page:", pageNumber); // Debug log
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      console.log("Going to previous page from", currentPage); // Debug log
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      console.log("Going to next page from", currentPage); // Debug log
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToRequestHistory = () => {
    navigate("/manage-advertising-agent");
  };

  const goToApprovedHistory = () => {
    navigate("/advertising-agent-approved-history");
  };

  const goToRejectedHistory = () => {
    navigate("/advertising-agent-rejected-history");
  };

  const handleRowClick = (application) => {
    navigate("/advertising-agent-details", {
      state: {
        agent: {
          ...application.customer_details,
          application_id: application.application_id,
          agent_type: application.agent_type,
          status: application.status,
          applied_at: application.applied_at,
          reviewed_at: application.reviewed_at,
          rejection_reason: application.rejection_reason,
          photo_url: application.photo_url,
          is_already_agent: application.is_already_agent,
        },
        application: application,
      },
    });
  };

  // Generate page buttons
  const renderPageButtons = () => {
    const buttons = [];
    const maxVisiblePages = 10;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're near the start
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className={`page-btn ${currentPage === 1 ? "active" : ""}`}
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="page-ellipsis">...</span>);
      }
    }
    
    // Page range
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`page-btn ${currentPage === i ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="page-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          className={`page-btn ${currentPage === totalPages ? "active" : ""}`}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    
    return buttons;
  };

  // Filter applications locally as fallback
  const filteredApplications = applications.filter((application) => {
    const customerName = application.customer_details 
      ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
      : application.first_name 
        ? `${application.first_name} ${application.last_name || ''}`.trim()
        : 'N/A';
    
    const customerEmail = application.customer_details?.email || application.email || 'N/A';

    const searchMatch =
      search === "" ||
      customerName.toLowerCase().includes(search.toLowerCase()) ||
      customerEmail.toLowerCase().includes(search.toLowerCase());

    const typeMatch =
      type === "all" ||
      application.agent_type?.toLowerCase() === type.toLowerCase();

    // Date filtering in dd/mm/yyyy format
    const appDate = formatToDDMMYYYY(application.reviewed_at);
    const dateMatch = approvedDate === "all" || appDate === approvedDate;

    return searchMatch && dateMatch && typeMatch;
  });

  // Calculate current page count
  const currentPageAgentsCount = filteredApplications.length;

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Ads Agents</h2>
          </div>

          {/* Approved History Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Approved:</span>
              <span className="stat-value">{totalApprovedAgents}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
          </div>


          <div className="agent-type-filters" style={{ display: "flex", flexWrap: "wrap", gap: "10px", margin: "10px 0 20px 0" }}>
            <button
              className="action-btn"
              onClick={() => setType("all")}
              style={{
                backgroundColor: type === "all" ? "rgba(0, 0, 160, 1)" : "#e4e6eb",
                color: type === "all" ? "white" : "black",
                margin: "0 10px 0 0"
              }}
            >
              List All
            </button>
            <button
              className="action-btn"
              onClick={() => setType("senior")}
              style={{
                backgroundColor: type === "senior" ? "rgba(0, 0, 160, 1)" : "#e4e6eb",
                color: type === "senior" ? "white" : "black",
                margin: "0 10px 0 0"
              }}
            >
              List Senior Ads Agents
            </button>
            <button
              className="action-btn"
              onClick={() => setType("mini")}
              style={{
                backgroundColor: type === "mini" ? "rgba(0, 0, 160, 1)" : "#e4e6eb",
                color: type === "mini" ? "white" : "black",
                margin: 0
              }}
            >
              List Mini Ads Agents
            </button>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by Approved Date:</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="date"
                  value={getHTMLDateValue()}
                  onChange={handleDateChange}
                  className="date-filter-select"
                 maxLength={255} />
                {approvedDate !== "all" && (
                  <button 
                    className="clear-date-btn"
                    onClick={() => setApprovedDate("all")}
                    style={{
                      marginLeft: '8px', 
                      padding: '4px 8px',
                      background: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={handleSearchChange}
                className="search-input"
               maxLength={20} />
            </div>
          </div>

          <div className="agent-top-bar">
            <div className="top-buttons">
              <button className="tab-btn" onClick={goToRequestHistory}>
                Request History
              </button>
              <button className="tab-btn active" onClick={goToApprovedHistory}>
                Approved History
              </button>
              <button className="tab-btn" onClick={goToRejectedHistory}>
                Rejected History
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Request Date</th>
                <th>Approved Date</th>
                <th>Status</th>
                {type !== "all" && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={type === "all" ? 6 : 7} style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Applications...</div>
                  </td>
                </tr>
              ) : filteredApplications.length > 0 ? (
                filteredApplications.map((application, i) => (
                  <tr
                    key={application.application_id || i}
                    onClick={() => handleRowClick(application)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{application.customer_details?.first_name} {application.customer_details?.last_name}</td>
                    <td>{application.customer_details?.email}</td>
                    <td>
                      {application.agent_type === "senior"
                        ? "Senior Ads Agent"
                        : "Mini Ads Agent"}
                    </td>
                    <td>{formatToDDMMYYYY(application.applied_at)}</td>
                    <td>{formatToDDMMYYYY(application.reviewed_at)}</td>
                    <td>
                      <span className={`status approved`}>Approved</span>
                    </td>
                    {type !== "all" && (
                      <td>
                        {application.assigned_card_name ? (
                          <span style={{ fontWeight: "bold", color: "#333", fontSize: "14px" }}>
                            {application.assigned_card_name}
                          </span>
                        ) : (
                          <button 
                            className="action-btn"
                            style={{ margin: 0, padding: "6px 12px", fontSize: "13px", borderRadius: "8px" }}
                            onClick={(e) => handleAssignClick(e, application)}
                          >
                            Assign
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={type === "all" ? 6 : 7} style={{ textAlign: "center" }}>
                    {error || (
                      filteredAgentsCount === 0 && totalApprovedAgents > 0 
                        ? "No approved applications match your filters" 
                        : "No approved applications found"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && totalPages > 0 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              
              {renderPageButtons()}
              
              <button
                className="page-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Assign Card Modal */}
      {assignModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%", 
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", 
          justifyContent: "center", alignItems: "center", zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white", padding: "30px", borderRadius: "10px", 
            width: "400px", maxWidth: "90%", fontFamily: "var(--font-header)"
          }}>
            <h3 style={{ marginTop: 0, fontSize: "20px", color: "rgba(1, 20, 14, 1)" }}>Assign Collectable Card</h3>
            <p style={{ color: "rgba(68, 71, 92, 1)", fontSize: "16px" }}>
              Assigning card to: <strong>{selectedAgentForCard?.customer_details?.first_name} {selectedAgentForCard?.customer_details?.last_name}</strong>
            </p>
            
            <div style={{ margin: "20px 0" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "16px", color: "rgba(1, 20, 14, 1)" }}>
                {isMiniAgent ? 'Select a "minicampaign" name:' : 'Select a "senior_legendary" name:'}
              </label>
              <select 
                value={selectedCard} 
                onChange={(e) => setSelectedCard(e.target.value)}
                style={{ 
                  width: "100%", padding: "10px", borderRadius: "8px", 
                  border: "1px solid rgba(68, 71, 92, 0.3)", fontSize: "15px",
                  color: "rgba(68, 71, 92, 1)", outline: "none"
                }}
              >
                <option value="">-- Choose a Card --</option>
                {isMiniAgent 
                  ? availableMiniCards.map(card => (
                      <option key={card.id} value={card.id}>{card.name}</option>
                    ))
                  : availableLegendaryCards.map(card => (
                      <option key={card.id} value={card.id}>{card.name}</option>
                    ))
                }
              </select>
              {(isMiniAgent ? availableMiniCards : availableLegendaryCards).length === 0 && (
                <p style={{ color: "red", fontSize: "12px", marginTop: "8px" }}>No more cards available.</p>
              )}
            </div>
            
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
              <button 
                onClick={() => setAssignModalOpen(false)}
                style={{ 
                  padding: "8px 15px", borderRadius: "17.6px", border: "1px solid rgba(68, 71, 92, 0.3)", 
                  background: "white", cursor: "pointer", fontSize: "14px", fontWeight: "600",
                  color: "rgba(68, 71, 92, 1)"
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleAssignCard}
                disabled={!selectedCard}
                style={{ 
                  padding: "8px 15px", borderRadius: "17.6px", border: "none", 
                  background: selectedCard ? "rgba(0, 0, 160, 1)" : "#ccc", 
                  color: "white", cursor: selectedCard ? "pointer" : "not-allowed",
                  fontSize: "14px", fontWeight: "600"
                }}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar 
        open={snackOpen} 
        autoHideDuration={4000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={handleSnackClose} 
          severity={snackSeverity}
          sx={{ width: '100%', ...(snackSeverity === 'success' && { backgroundColor: '#33b233' }) }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default AdvertisingAgentApprovedHistory;
